
import { useState, useCallback } from 'react';
import { toast } from '@/components/ui/sonner';
import { 
  deleteSubscription,
  updateSubscriptionStatus 
} from '@/pages/admin/subscriptions/api/subscriptionApi';

export const useSubscriptionActions = (
  fetchData: () => Promise<void>,
  handleCloseDialog: () => void,
  selectedSubscription: string | null
) => {
  const [actionLoading, setActionLoading] = useState(false);
  // Добавляем состояние для отслеживания режима редактирования
  const [isEditMode, setIsEditMode] = useState(false);
  
  const handleStatusUpdate = useCallback(async (
    id: string, 
    status: 'active' | 'cancelled' | 'paused'
  ) => {
    setActionLoading(true);
    try {
      const result = await updateSubscriptionStatus(id, status);
      if (result.success) {
        toast.success(`Статус подписки успешно изменен на ${status}`, {
          duration: 5000 // 5 секунд для уведомления
        });
        
        // Закрываем диалог перед обновлением данных
        handleCloseDialog();
        
        // Добавляем небольшую задержку перед обновлением данных
        setTimeout(() => {
          fetchData();
        }, 300);
      } else {
        const errorMessage = 'error' in result ? result.error : 'Неизвестная ошибка';
        toast.error(`Не удалось изменить статус подписки: ${errorMessage}`, {
          duration: 5000 // 5 секунд для уведомления
        });
      }
    } catch (err: any) {
      console.error('Ошибка при обновлении статуса:', err);
      toast.error('Произошла ошибка при обновлении статуса', {
        duration: 5000 // 5 секунд для уведомления
      });
    } finally {
      setActionLoading(false);
    }
  }, [fetchData, handleCloseDialog]);

  const handleDeleteSubscription = useCallback(async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить эту подписку?')) return;
    
    setActionLoading(true);
    try {
      const result = await deleteSubscription(id);
      if (result.success) {
        toast.success('Подписка успешно удалена', {
          duration: 5000 // 5 секунд для уведомления
        });
        
        // Сначала закрываем диалог, если удалена текущая подписка
        if (selectedSubscription === id) {
          handleCloseDialog();
        }
        
        // Добавляем небольшую задержку перед обновлением данных
        setTimeout(() => {
          fetchData();
        }, 300);
      } else {
        const errorMessage = 'error' in result ? result.error : 'Неизвестная ошибка';
        toast.error(`Не удалось удалить подписку: ${errorMessage}`, {
          duration: 5000 // 5 секунд для уведомления
        });
      }
    } catch (err: any) {
      console.error('Ошибка при удалении подписки:', err);
      toast.error('Произошла ошибка при удалении подписки', {
        duration: 5000 // 5 секунд для уведомления
      });
    } finally {
      setActionLoading(false);
    }
  }, [fetchData, handleCloseDialog, selectedSubscription]);
  
  // Добавляем новую функцию для включения режима редактирования
  const handleEditSubscription = useCallback(() => {
    console.log('Включаем режим редактирования для подписки:', selectedSubscription);
    setIsEditMode(true);
  }, [selectedSubscription]);
  
  // Добавляем функцию для выключения режима редактирования
  const handleCloseEditMode = useCallback(() => {
    setIsEditMode(false);
  }, []);

  return {
    actionLoading,
    isEditMode,
    handleStatusUpdate,
    handleDeleteSubscription,
    handleEditSubscription,
    handleCloseEditMode
  };
};
